import type {
    EcommerceSignalColumnCellDto,
    EcommerceSignalDataRowItem,
} from "../models/ecommerce/EcommerceSignalColumnCellDto";
import type { NormalizedEcommerceSignalColumnValue } from "../types/columns";

type NormalizeEcommerceSignalColumnOptions = {
    /**
     * When false, string values are treated as legacy display text (no JSON.parse).
     * Set to false after a successful parse so nested string payloads cannot recurse indefinitely.
     */
    readonly allowJsonStringParse?: boolean;
};

const normalizeApiRowItem = (raw: unknown): EcommerceSignalDataRowItem | null => {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
    }

    const o = raw as Record<string, unknown>;
    const metric = typeof o.metric === "string" ? o.metric : "";
    const direction = typeof o.direction === "string" ? o.direction : "";
    const previous_value =
        typeof o.previous_value === "string"
            ? o.previous_value
            : typeof o.previousValue === "string"
              ? o.previousValue
              : "";
    const new_value = typeof o.new_value === "string" ? o.new_value : typeof o.newValue === "string" ? o.newValue : "";

    if (!metric && !new_value && !previous_value) {
        return null;
    }

    return { metric, direction, previous_value, new_value };
};

const normalizeToApiRowItems = (raw: readonly unknown[]): readonly EcommerceSignalDataRowItem[] | null => {
    const items = raw.map(normalizeApiRowItem).filter((row): row is EcommerceSignalDataRowItem => row !== null);
    return items.length > 0 ? items : null;
};

const normalizeLegacyObject = (raw: Record<string, unknown>): EcommerceSignalColumnCellDto => {
    const change =
        typeof raw.change === "number" ? raw.change : typeof raw.changePercent === "number" ? raw.changePercent : null;
    const currentDisplayValue =
        typeof raw.currentDisplayValue === "string"
            ? raw.currentDisplayValue
            : typeof raw.current_value === "string"
              ? raw.current_value
              : typeof raw.currentValue === "string"
                ? raw.currentValue
                : null;
    const previousDisplayValue =
        typeof raw.previousDisplayValue === "string"
            ? raw.previousDisplayValue
            : typeof raw.previous_value === "string"
              ? raw.previous_value
              : typeof raw.previousValue === "string"
                ? raw.previousValue
                : null;

    return {
        change,
        currentDisplayValue,
        previousDisplayValue,
    };
};

export const normalizeEcommerceSignalColumnCellValue = (
    raw: unknown,
    options?: NormalizeEcommerceSignalColumnOptions,
): NormalizedEcommerceSignalColumnValue => {
    const allowJsonStringParse = options?.allowJsonStringParse ?? true;

    if (raw === null || typeof raw === "undefined") {
        return null;
    }

    if (typeof raw === "number") {
        return raw;
    }

    if (typeof raw === "string") {
        if (!allowJsonStringParse) {
            return { currentDisplayValue: raw };
        }
        const trimmed = raw.trim();
        if (trimmed === "") {
            return null;
        }
        try {
            const parsed: unknown = JSON.parse(trimmed);
            return normalizeEcommerceSignalColumnCellValue(parsed, { allowJsonStringParse: false });
        } catch {
            return { currentDisplayValue: raw };
        }
    }

    if (Array.isArray(raw)) {
        return normalizeToApiRowItems(raw);
    }

    if (typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        if (typeof o.metric === "string") {
            const row = normalizeApiRowItem(raw);
            return row ? [row] : null;
        }
        return normalizeLegacyObject(o);
    }

    return null;
};
